'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { Activity, BarChart2, Cpu, AlignLeft, Layers, Clock, Settings, X, Check, Info, Database, TrendingUp } from 'lucide-react';
import { 
  ResponsiveContainer, LineChart, Line, BarChart, Bar, 
  XAxis, YAxis, Tooltip, CartesianGrid 
} from 'recharts';
import { colors, baseStyles, formatNum } from './SharedUI';
import CandleAnalyzer from './CandleAnalyzer';

import { 
  fetchAndAggregateData, 
  fetchOpenInterestHistory, 
  fetchFundingRateHistory, 
  fetchCurrentOI 
} from './Util'; 

const DEFAULT_ENGINE_CONFIG = {
  marubozuBodyRatio: 0.90,     
  strongBodyRatio: 0.60,       
  stopHuntWickRatio: 0.50,     
  volatileMultiplier: 1.5,     
  dojiBodyMax: 0.10,           
  exhaustedVolMax: 0.8,        
  weakBodyRatio: 0.30,         
  pumpDumpTakerPct: 60,        
  compressionMultiplier: 0.50, 
};

const ALGO_DICT = {
  PUMP: { short: 'PUMP', desc: 'Phe mua đẩy giá lên mạnh.', color: '#06b6d4', confidence: 'HIGH', logic: 'Body lớn + Close near High' },
  STRONG_BULL: { short: 'S-BULL', desc: 'Buyer kiểm soát mạnh.', color: '#0ea5e9', confidence: 'HIGH', logic: 'Body/Range cao' },
  WEAK_BULL: { short: 'W-BULL', desc: 'Đà tăng yếu.', color: '#38bdf8', confidence: 'HIGH', logic: 'Body nhỏ nhưng vẫn bullish' },
  MARUBOZU_BULL: { short: 'MBULL', desc: 'Bullish cực mạnh gần như không có râu.', color: '#0284c7', confidence: 'VERY_HIGH', logic: 'Full body candle' },
  DUMP: { short: 'DUMP', desc: 'Phe bán đẩy giá xuống mạnh.', color: '#ef4444', confidence: 'HIGH', logic: 'Body lớn + Close near Low' },
  STRONG_BEAR: { short: 'S-BEAR', desc: 'Seller kiểm soát mạnh.', color: '#dc2626', confidence: 'HIGH', logic: 'Body/Range cao' },
  WEAK_BEAR: { short: 'W-BEAR', desc: 'Đà giảm yếu.', color: '#f87171', confidence: 'HIGH', logic: 'Body nhỏ nhưng vẫn bearish' },
  MARUBOZU_BEAR: { short: 'MBEAR', desc: 'Bearish cực mạnh gần như không có râu.', color: '#991b1b', confidence: 'VERY_HIGH', logic: 'Full body candle' },
  SHOOTING_STAR: { short: 'STAR-H', desc: 'Reject mạnh phía trên.', color: '#fb7185', confidence: 'VERY_HIGH', logic: 'Upper wick >> Body' },
  UPPER_REJECTION: { short: 'UP-REJ', desc: 'Bị từ chối vùng giá phía trên.', color: '#f43f5e', confidence: 'HIGH', logic: 'Upper wick lớn' },
  HAMMER: { short: 'HAMMER', desc: 'Reject mạnh phía dưới.', color: '#4ade80', confidence: 'VERY_HIGH', logic: 'Lower wick >> Body' },
  LOWER_REJECTION: { short: 'LOW-REJ', desc: 'Bị từ chối vùng giá phía dưới.', color: '#22c55e', confidence: 'HIGH', logic: 'Lower wick lớn' },
  BALANCED: { short: 'BALNC', desc: 'Không phe nào kiểm soát rõ.', color: '#64748b', confidence: 'HIGH', logic: 'Body trung tính' },
  SIDEWAY: { short: 'SIDE', desc: 'Đi ngang thiếu động lượng.', color: '#e2e8f0', confidence: 'HIGH', logic: 'Range nhỏ' },
  SPINNING_TOP: { short: 'SPIN', desc: 'Do dự mạnh với wick hai đầu.', color: '#94a3b8', confidence: 'VERY_HIGH', logic: 'Body nhỏ + Wick 2 đầu' },
  INSIDE_BAR: { short: 'INSIDE', desc: 'Nến nằm hoàn toàn trong nến trước.', color: '#64748b', confidence: 'VERY_HIGH', logic: 'Inside previous candle' },
  HIGH_VOLATILITY: { short: 'H-VOL', desc: 'Biến động mạnh.', color: '#8b5cf6', confidence: 'VERY_HIGH', logic: 'Range tăng mạnh' },
  WIDE_RANGE_CANDLE: { short: 'W-RNG', desc: 'Nến có biên độ rất lớn.', color: '#7c3aed', confidence: 'VERY_HIGH', logic: 'Range >> Average range' },
  VOLATILE_BALANCE: { short: 'V-BAL', desc: 'Biến động lớn nhưng thiếu hướng đi rõ.', color: '#9333ea', confidence: 'VERY_HIGH', logic: 'Large range + Small body' },
  COMPRESSION_PHASE: { short: 'COMP', desc: 'Volatility co hẹp dần.', color: '#c084fc', confidence: 'HIGH', logic: 'Range shrink liên tục' },
  EXPANSION_MOVE: { short: 'EXPAND', desc: 'Volatility mở rộng mạnh.', color: '#8b5cf6', confidence: 'HIGH', logic: 'Range expansion liên tục' },
  CLEAN_BREAKOUT_UP: { short: 'BO-UP', desc: 'Breakout tăng rõ ràng.', color: '#06b6d4', confidence: 'HIGH', logic: 'Close vượt resistance' },
  CLEAN_BREAKOUT_DOWN: { short: 'BO-DN', desc: 'Breakout giảm rõ ràng.', color: '#ef4444', confidence: 'HIGH', logic: 'Close phá support' },
  EXHAUSTED: { short: 'EXHST', desc: 'Mất động lượng.', color: '#84cc16', confidence: 'HIGH', logic: 'Small spread + Small body' },
  DOJI: { short: 'DOJI', desc: 'Do dự cực mạnh.', color: '#a3e635', confidence: 'VERY_HIGH', logic: 'Open gần bằng Close' }
};

const CATEGORY_GROUPS = [
  { id: 'TREND_UP', name: 'XU HƯỚNG TĂNG', keys: ['MARUBOZU_BULL', 'PUMP', 'STRONG_BULL', 'WEAK_BULL'] },
  { id: 'TREND_DOWN', name: 'XU HƯỚNG GIẢM', keys: ['MARUBOZU_BEAR', 'DUMP', 'STRONG_BEAR', 'WEAK_BEAR'] },
  { id: 'BREAKOUT', name: 'PHÁ VỠ (BREAKOUT)', keys: ['CLEAN_BREAKOUT_UP', 'CLEAN_BREAKOUT_DOWN'] },
  { id: 'REJECTION_LOWER', name: 'TỪ CHỐI GIẢM (RÂU DƯỚI)', keys: ['HAMMER', 'LOWER_REJECTION'] },
  { id: 'REJECTION_UPPER', name: 'TỪ CHỐI TĂNG (RÂU TRÊN)', keys: ['SHOOTING_STAR', 'UPPER_REJECTION'] },
  { id: 'VOLATILE', name: 'BIẾN ĐỘNG MẠNH', keys: ['WIDE_RANGE_CANDLE', 'HIGH_VOLATILITY', 'VOLATILE_BALANCE', 'EXPANSION_MOVE', 'COMPRESSION_PHASE'] },
  { id: 'BALANCED', name: 'CÂN BẰNG & TÍCH LŨY', keys: ['INSIDE_BAR', 'SPINNING_TOP', 'SIDEWAY', 'BALANCED'] },
  { id: 'EXHAUSTED', name: 'KIỆT SỨC / DO DỰ', keys: ['DOJI', 'EXHAUSTED'] }
];

const formatUSD = (val) => {
  const sign = val < 0 ? '-' : '';
  const absVal = Math.abs(val);

  if (absVal >= 1000000) return `${sign}$${(absVal / 1000000).toFixed(2)}M`;
  if (absVal >= 1000) return `${sign}$${(absVal / 1000).toFixed(1)}k`;
  return `${sign}$${absVal.toFixed(0)}`;
};

export default function MultiTimeframeMatrix({ symbol: initialSymbol = 'BTCUSDT' }) {
  const [symbol, setSymbol] = useState(initialSymbol);
  const [inputSymbol, setInputSymbol] = useState(initialSymbol);
  const [lookback, setLookback] = useState(1);
  const [displayLookback, setDisplayLookback] = useState(1);
  const intervals = [1, 2, 4, 6, 8, 12];
  
  const [matrixData, setMatrixData] = useState({});
  const [rawKlines, setRawKlines] = useState({});
  const [selectedCandle, setSelectedCandle] = useState(null);
  
  const [agg5mData, setAgg5mData] = useState({});
  const [agg15mData, setAgg15mData] = useState({});
  
  const [mc5mData, setMc5mData] = useState({ currentOI: null, intervals: {} });
  const [mc15mData, setMc15mData] = useState({ currentOI: null, intervals: {} });

  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [showSettings, setShowSettings] = useState(false);
  const [engineConfig, setEngineConfig] = useState(DEFAULT_ENGINE_CONFIG); 
  const [draftConfig, setDraftConfig] = useState(DEFAULT_ENGINE_CONFIG);   

  useEffect(() => {
    if (initialSymbol) {
      const cleanSymbol = initialSymbol.trim().toUpperCase();
      setSymbol(cleanSymbol);
      setInputSymbol(cleanSymbol);
      
      setMatrixData({});
      setAgg5mData({});
      setAgg15mData({});
      setSelectedCandle(null);
      setHasSearched(false);
      setMc5mData({ currentOI: null, intervals: {} });
      setMc15mData({ currentOI: null, intervals: {} });
    }
  }, [initialSymbol]);

  const handleOpenSettings = () => {
    setDraftConfig(engineConfig);
    setShowSettings(true);
  };

  const handleCancelSettings = () => setShowSettings(false);

  const handleApplyConfig = () => {
    const safeConfig = {
      marubozuBodyRatio: parseFloat(draftConfig.marubozuBodyRatio) || DEFAULT_ENGINE_CONFIG.marubozuBodyRatio,
      strongBodyRatio: parseFloat(draftConfig.strongBodyRatio) || DEFAULT_ENGINE_CONFIG.strongBodyRatio,
      stopHuntWickRatio: parseFloat(draftConfig.stopHuntWickRatio) || DEFAULT_ENGINE_CONFIG.stopHuntWickRatio,
      volatileMultiplier: parseFloat(draftConfig.volatileMultiplier) || DEFAULT_ENGINE_CONFIG.volatileMultiplier,
      dojiBodyMax: parseFloat(draftConfig.dojiBodyMax) || DEFAULT_ENGINE_CONFIG.dojiBodyMax,
      exhaustedVolMax: parseFloat(draftConfig.exhaustedVolMax) || DEFAULT_ENGINE_CONFIG.exhaustedVolMax,
      weakBodyRatio: parseFloat(draftConfig.weakBodyRatio) || DEFAULT_ENGINE_CONFIG.weakBodyRatio,
      pumpDumpTakerPct: parseFloat(draftConfig.pumpDumpTakerPct) || DEFAULT_ENGINE_CONFIG.pumpDumpTakerPct,
      compressionMultiplier: parseFloat(draftConfig.compressionMultiplier) || DEFAULT_ENGINE_CONFIG.compressionMultiplier,
    };

    setEngineConfig(safeConfig);
    setDraftConfig(safeConfig);
    setShowSettings(false);
    alert('Cấu hình đã lưu! Vui lòng bấm thực thi nút Quy Đổi hoặc Tìm Quá Khứ để quét lại.');
  };

  const analyzeCandle = (kline, index, intervalHours, totalCandles, yesterdayData = {}, cfg = DEFAULT_ENGINE_CONFIG) => {
    const activeColors = (typeof colors !== 'undefined') ? colors : { green: '#089981', red: '#f23645' };
    const safeFormatNum = (typeof formatNum === 'function') ? formatNum : (val, dec) => val.toFixed(dec);

    const ts = kline[0];
    const open = parseFloat(kline[1]) || 0;
    const high = parseFloat(kline[2]) || 0;
    const low = parseFloat(kline[3]) || 0;
    const close = parseFloat(kline[4]) || 0;
    const volume = parseFloat(kline[5]) || 0;
    const quoteVolume = parseFloat(kline[7]) || 0;
    const trades = parseInt(kline[8]) || 0;
    const takerBuyVol = parseFloat(kline[9]) || 0;
    const takerBuyQuoteVol = parseFloat(kline[10]) || 0;
    if (open <= 0 || high < low) return null;

    const takerSellVol = Math.max(0, volume - takerBuyVol);
    const takerSellQuoteVol = Math.max(0, quoteVolume - takerBuyQuoteVol);
    const takerBuyPct = volume > 0 ? (takerBuyVol / volume) * 100 : 50;
    const takerSellPct = 100 - takerBuyPct;

    const isGreen = close >= open;
    const spreadPct = ((close - open) / open) * 100;
    const absSpread = Math.abs(spreadPct);
    const rangePct = low > 0 ? ((high - low) / low) * 100 : 0;
    const bodySize = Math.abs(close - open);
    const totalRange = high - low;
    const upperWick = high - Math.max(open, close);
    const lowerWick = Math.min(open, close) - low;

    const bodyRatio = totalRange > 0 ? bodySize / totalRange : 0;
    const lowerWickRatioPA = totalRange > 0 ? lowerWick / totalRange : 0;
    const upperWickRatioPA = totalRange > 0 ? upperWick / totalRange : 0;

    let wickType = 'Bình thường';
    if (bodyRatio <= cfg.dojiBodyMax) wickType = 'Doji';
    else if (lowerWickRatioPA >= cfg.stopHuntWickRatio && lowerWickRatioPA > upperWickRatioPA) wickType = 'Râu dưới dài';
    else if (upperWickRatioPA >= cfg.stopHuntWickRatio && upperWickRatioPA > lowerWickRatioPA) wickType = 'Râu trên dài';

    const fallbackAtr = Math.max(Math.sqrt(intervalHours) * 0.75, 0.3);
    const atr = yesterdayData?.atr14 || fallbackAtr;
    const dynThreshold = Math.max(atr * 0.75, 0.3);
    const smaVol = yesterdayData?.smaVolume20 || volume;
    const rVol = smaVol > 0 ? volume / smaVol : 1;

    const prevHigh = yesterdayData?.prevHigh || high;
    const prevLow = yesterdayData?.prevLow || low;
    const prevRangePct = yesterdayData?.prevRangePct || rangePct;

    const s = Object.keys(ALGO_DICT).reduce((acc, key) => { acc[key] = 0; return acc; }, {});
    const isVolatile = absSpread >= (dynThreshold * cfg.volatileMultiplier);
    const isCompression = absSpread <= (dynThreshold * cfg.compressionMultiplier);

    if (isGreen) {
      if (bodyRatio >= cfg.marubozuBodyRatio) s.MARUBOZU_BULL += 85 + (bodyRatio * 10);
      else if (bodyRatio >= cfg.strongBodyRatio) s.STRONG_BULL += 75 + (bodyRatio * 10);
      else if (bodyRatio >= cfg.weakBodyRatio) s.WEAK_BULL += 60;
      if (absSpread > dynThreshold * 1.2 && takerBuyPct > cfg.pumpDumpTakerPct) s.PUMP += 80;
      if (close > prevHigh && absSpread > dynThreshold) s.CLEAN_BREAKOUT_UP += 85;
    } else {
      if (bodyRatio >= cfg.marubozuBodyRatio) s.MARUBOZU_BEAR += 85 + (bodyRatio * 10);
      else if (bodyRatio >= cfg.strongBodyRatio) s.STRONG_BEAR += 75 + (bodyRatio * 10);
      else if (bodyRatio >= cfg.weakBodyRatio) s.WEAK_BEAR += 60;
      if (absSpread > dynThreshold * 1.2 && takerSellPct > cfg.pumpDumpTakerPct) s.DUMP += 80;
      if (close < prevLow && absSpread > dynThreshold) s.CLEAN_BREAKOUT_DOWN += 85;
    }

    if (lowerWickRatioPA >= cfg.stopHuntWickRatio && lowerWickRatioPA > upperWickRatioPA) {
      s.HAMMER += 80 + (lowerWickRatioPA * 15);
    } else if (lowerWickRatioPA >= 0.3) {
      s.LOWER_REJECTION += 65 + (lowerWickRatioPA * 10);
    }

    if (upperWickRatioPA >= cfg.stopHuntWickRatio && upperWickRatioPA > lowerWickRatioPA) {
      s.SHOOTING_STAR += 80 + (upperWickRatioPA * 15);
    } else if (upperWickRatioPA >= 0.3) {
      s.UPPER_REJECTION += 65 + (upperWickRatioPA * 10);
    }

    if (bodyRatio <= cfg.dojiBodyMax) {
      s.DOJI += 85;
      if (rVol < cfg.exhaustedVolMax && isCompression) s.EXHAUSTED += 80;
    } else if (bodyRatio <= 0.25 && lowerWickRatioPA >= 0.2 && upperWickRatioPA >= 0.2) {
      s.SPINNING_TOP += 75;
    }

    if (high <= prevHigh && low >= prevLow && index > 0) {
      s.INSIDE_BAR += 85;
    }

    if (isVolatile) {
      s.HIGH_VOLATILITY += 70;
      s.WIDE_RANGE_CANDLE += 75;
      if (bodyRatio <= 0.3) s.VOLATILE_BALANCE += 80;
      if (rangePct > prevRangePct * 1.5) s.EXPANSION_MOVE += 65;
    }

    if (isCompression) {
      s.SIDEWAY += 70;
      if (rangePct < prevRangePct * 0.8) s.COMPRESSION_PHASE += 75;
    }

    if (Math.max(...Object.values(s)) < 40) {
      s.BALANCED = 60;
    }

    const entries = Object.entries(s).sort((a, b) => b[1] - a[1]);
    const topState = entries[0][0];
    const dateObj = new Date(ts);
    const candlesPerDay = 24 / intervalHours;
    const safeDayLabel = candlesPerDay > 0 ? Math.floor(index / candlesPerDay) + 1 : 1;
    const hourLabel = String(dateObj.getHours()).padStart(2, '0');

    const scoreRunnerUp = entries[1] ? entries[1][1] : 0;
    const confidenceScore = Math.max(0, Math.min(100, entries[0][1] + (entries[0][1] - scoreRunnerUp) * 0.2)).toFixed(1);

    return {
      id: `${intervalHours}H-${ts}`,
      intervalVal: intervalHours,
      interval: `${intervalHours}H`,
      time: `Ngày ${safeDayLabel} - ${hourLabel}:00`,
      realTime: dateObj.toLocaleString(), 
      state: topState,
      confidence: parseFloat(confidenceScore),
      isGreen,
      candleColor: isGreen ? activeColors.green : activeColors.red,
      spreadStr: `${spreadPct >= 0 ? '+' : ''}${spreadPct.toFixed(2)}%`,
      yesterdayData: yesterdayData,
      details: {
        openTime: ts,
        spread: `${spreadPct >= 0 ? '+' : ''}${spreadPct.toFixed(2)}%`,
        wick: wickType,
        takerBuy: takerBuyPct.toFixed(1),
        takerSell: takerSellPct.toFixed(1),
        trades, volume, quoteVolume,
        open, high, low, close,
        takerBuyVol, takerSellVol, takerBuyQuoteVol, takerSellQuoteVol,
        rangePct: rangePct.toFixed(3),
        rVol: rVol.toFixed(2),
        dynThreshold: dynThreshold.toFixed(2),
        bodyRatio, 
        spreadPctRaw: spreadPct, takerBuyPctRaw: takerBuyPct, takerSellPctRaw: takerSellPct,
        absSpreadRaw: absSpread,
        lowerWickRatio: lowerWickRatioPA, upperWickRatio: upperWickRatioPA,
        wickType, 
        avgVolCoin: trades > 0 ? (volume / trades) : 0,
        avgBuyCoin: trades > 0 ? (takerBuyVol / trades) : 0,
        avgSellCoin: trades > 0 ? (takerSellVol / trades) : 0,
        avgVol: trades > 0 ? safeFormatNum(volume / trades, 4) : '0',
        avgBuy: trades > 0 ? safeFormatNum(takerBuyVol / trades, 4) : '0',
        avgSell: trades > 0 ? safeFormatNum(takerSellQuoteVol / trades, 4) : '0',
        avgBuyQuote: trades > 0 ? safeFormatNum(takerBuyQuoteVol / trades, 4) : '0',
        avgSellQuote: trades > 0 ? safeFormatNum(takerSellQuoteVol / trades, 4) : '0'
      }
    };
  };

  const buildMatrixFromRaw = (rawDataset, configToUse) => {
    const newMatrix = {};
    Object.keys(rawDataset).forEach(invStr => {
      const inv = parseInt(invStr);
      const arr = rawDataset[invStr];
      const limit = arr.length;

      newMatrix[inv] = arr.map((kline, i) => {
        let sumVol = 0, volCount = 0, sumSpread = 0, spreadCount = 0;
        for (let j = 1; j <= 20; j++) {
          if (i - j >= 0) {
            const prevKline = arr[i - j];
            sumVol += parseFloat(prevKline[5]) || 0;
            volCount++;
            if (j <= 14) {
              const pOpen = parseFloat(prevKline[1]);
              const pClose = parseFloat(prevKline[4]);
              if (pOpen > 0) {
                sumSpread += Math.abs(((pClose - pOpen) / pOpen) * 100);
                spreadCount++;
              }
            }
          }
        }
        const prevKlineInfo = arr[i - 1];
        const pHigh = prevKlineInfo ? parseFloat(prevKlineInfo[2]) : null;
        const pLow = prevKlineInfo ? parseFloat(prevKlineInfo[3]) : null;
        const pRangePct = (pLow > 0) ? ((pHigh - pLow) / pLow) * 100 : null;

        const autoContextData = {
          smaVolume20: volCount > 0 ? sumVol / volCount : parseFloat(kline[5]),
          atr14: spreadCount > 0 ? sumSpread / spreadCount : null,
          prevHigh: pHigh, prevLow: pLow, prevRangePct: pRangePct
        };
        return analyzeCandle(kline, i, inv, limit, autoContextData, configToUse);
      }).filter(Boolean);
    });
    setMatrixData(newMatrix);
    if (newMatrix[1] && newMatrix[1].length > 0) setSelectedCandle(newMatrix[1][newMatrix[1].length - 1]);
  };

  const handleSearch = async () => {
    if (!inputSymbol.trim()) return;
    const targetCoin = inputSymbol.trim().toUpperCase();
    
    setSymbol(targetCoin);
    setDisplayLookback(lookback);
    setIsLoading(true);
    
    try {
      const newRaws = {};
      await Promise.all(intervals.map(async (inv) => {
        const limit = Math.floor((lookback * 24) / inv);
        const res = await fetch(`https://fapi.binance.com/fapi/v1/klines?symbol=${targetCoin}&interval=${inv}h&limit=${limit}`);
        if (!res.ok) throw new Error('API Error');
        newRaws[inv] = await res.json();
      }));
      setRawKlines(newRaws); 
      buildMatrixFromRaw(newRaws, engineConfig); 
      setHasSearched(true);
    } catch (err) {
      alert('Lỗi lấy dữ liệu quá khứ từ Binance. Hãy kiểm tra lại mã Token!');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExecuteAggregate = async (baseInterval) => {
    if (!inputSymbol.trim()) return;
    const targetCoin = inputSymbol.trim().toUpperCase();
    
    setSymbol(targetCoin);
    setIsLoading(true);
    
    try {
      const baseMin = baseInterval === '5m' ? 5 : 15;
      const totalMs = 500 * baseMin * 60 * 1000;
      const endTimeLimit = Date.now();
      const startTimeLimit = endTimeLimit - totalMs;

      const oiPeriod = baseInterval === '5m' ? '5m' : '15m';

      const [result, oiHistRes, fundHistRes, currentOiRes] = await Promise.all([
        fetchAndAggregateData(targetCoin, baseInterval, 500),
        fetchOpenInterestHistory(targetCoin, oiPeriod, 500),
        fetchFundingRateHistory(targetCoin, startTimeLimit, endTimeLimit, 100), 
        fetchCurrentOI(targetCoin)
      ]);

      if (result.success) {
        const analyzedAggMatrix = {};
        const matchedContext = { 
          currentOI: currentOiRes.success ? parseFloat(currentOiRes.data.openInterest) : null, 
          intervals: {} 
        };

        const oiHistData = oiHistRes.success ? oiHistRes.data : [];
        const fundHistData = fundHistRes.success ? fundHistRes.data : [];

        Object.keys(result.data).forEach((invStr) => {
          const inv = parseInt(invStr);
          const singleKlineArray = result.data[invStr];
          
          if (singleKlineArray && singleKlineArray.length > 0) {
            const rawCandle = singleKlineArray[0];
            const analyzed = analyzeCandle(rawCandle, 0, inv, 1, {}, engineConfig);
            if (analyzed) analyzedAggMatrix[inv] = [analyzed];

            const openTime = rawCandle[0];
            
            let matchedOI = null;
            if (oiHistData.length > 0) {
              const closestOI = oiHistData.reduce((prev, curr) => 
                Math.abs(curr.timestamp - openTime) < Math.abs(prev.timestamp - openTime) ? curr : prev
              );
              matchedOI = parseFloat(closestOI.sumOpenInterest);
            }

            let matchedFunding = null;
            if (fundHistData.length > 0) {
              const closestFund = fundHistData.reduce((prev, curr) => 
                Math.abs(curr.fundingTime - openTime) < Math.abs(prev.fundingTime - openTime) ? curr : prev
              );
              matchedFunding = parseFloat(closestFund.fundingRate);
            }

            matchedContext.intervals[inv] = { oldOI: matchedOI, funding: matchedFunding };
          }
        });
        
        if (baseInterval === '5m') {
          setAgg5mData(analyzedAggMatrix);
          setMc5mData(matchedContext);
          if (analyzedAggMatrix[1]?.[0]) setSelectedCandle(analyzedAggMatrix[1][0]);
        } else {
          setAgg15mData(analyzedAggMatrix);
          setMc15mData(matchedContext);
          if (analyzedAggMatrix[1]?.[0]) setSelectedCandle(analyzedAggMatrix[1][0]);
        }

        setHasSearched(true);
      } else {
        throw new Error(result.error);
      }
    } catch (err) {
      alert(`Lỗi xử lý dữ liệu gộp ${baseInterval.toUpperCase()}: Có thể mã Token không tồn tại Futures!`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLookbackChange = (days) => setLookback(days);

  const baseStats = useMemo(() => {
    if (!matrixData[1]) return { states: {}, green: 0, red: 0, total: 0 };
    const stats = { states: {}, green: 0, red: 0, total: matrixData[1].length };
    Object.keys(ALGO_DICT).forEach(k => stats.states[k] = 0);
    matrixData[1].forEach(c => {
      if(stats.states[c.state] !== undefined) stats.states[c.state]++;
      if (c.isGreen) stats.green++; else stats.red++;
    });
    return stats;
  }, [matrixData]);

  // ==========================================================
  // THUẬT TOÁN TÍNH TOÁN VISUAL ANALYTICS & TÍCH HỢP TỪ YAHOO SANG BINANCE
  // ==========================================================
  
  const chartDataArray = useMemo(() => {
    // Trích xuất dữ liệu gốc nến 1H (Interval nhỏ nhất) từ Object mảng rawKlines
    if (!rawKlines[1] || rawKlines[1].length === 0) return [];
    
    return rawKlines[1].map(kline => {
      const ms = kline[0];
      const dateObj = new Date(ms);
      const enDay = dateObj.toLocaleDateString('en-US', { weekday: 'long' });
      
      return {
        ms,
        timeLabel: dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        dateLabel: dateObj.toLocaleDateString([], { month: '2-digit', day: '2-digit' }),
        open: parseFloat(kline[1]),
        high: parseFloat(kline[2]),
        low: parseFloat(kline[3]),
        close: parseFloat(kline[4]),
        volume: parseFloat(kline[5]),
        estDayName: enDay,
        estHourIndex: dateObj.getHours() // Sử dụng trực tiếp giờ hệ thống thiết bị
      };
    });
  }, [rawKlines]);

  const volumeProfileData = useMemo(() => {
    if (chartDataArray.length === 0) return [];
    const prices = chartDataArray.map(b => b.close);
    const maxPrice = Math.max(...prices);
    const minPrice = Math.min(...prices);
    const totalBins = 12;
    const binSize = (maxPrice - minPrice) / totalBins;
    
    const bins = Array.from({ length: totalBins }, (_, i) => {
      const low = minPrice + (i * binSize);
      const high = low + binSize;
      return { priceLevel: ((low + high) / 2).toFixed(2), volume: 0, low, high };
    });

    chartDataArray.forEach(bar => {
      for (let i = 0; i < totalBins; i++) {
        if (bar.close >= bins[i].low && bar.close <= bins[i].high) {
          bins[i].volume += bar.volume;
          break;
        }
      }
    });
    return bins;
  }, [chartDataArray]);

  const heatmapMatrix = useMemo(() => {
    if (chartDataArray.length === 0) return null;
    
    const daysEn = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const daysVn = ['Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7', 'CN'];
    
    // Crypto hoạt động 24/7 thay vì 8-17 như Chứng Khoán
    const tradingHours = Array.from({ length: 24 }, (_, i) => i); 

    const matrix = {};
    daysEn.forEach(d => {
      matrix[d] = {};
      tradingHours.forEach(h => { matrix[d][h] = 0; });
    });

    chartDataArray.forEach(bar => {
      if (matrix[bar.estDayName] && matrix[bar.estDayName][bar.estHourIndex] !== undefined) {
        matrix[bar.estDayName][bar.estHourIndex] += bar.volume;
      }
    });

    let maxVolumeInGrid = 1;
    daysEn.forEach(d => {
      tradingHours.forEach(h => {
        if (matrix[d][h] > maxVolumeInGrid) maxVolumeInGrid = matrix[d][h];
      });
    });

    return { matrix, daysEn, daysVn, tradingHours, maxVolumeInGrid };
  }, [chartDataArray]);

  // ==========================================================

  const statBox = (label, value, valueColor = '#ffffff', subValue = null) => (
    <div style={{ backgroundColor: 'rgba(0,0,0,0.4)', border: `1px solid ${colors.border}`, padding: '10px 12px', borderRadius: '6px', display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
      <div style={{ fontSize: '11px', color: '#fbbf24', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>{label}</div>
      <div style={{ fontSize: '14px', fontWeight: '900', color: valueColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</div>
      {subValue && <div style={{ fontSize: '11px', color: '#ffffff', fontWeight: '700', whiteSpace: 'nowrap' }}>{subValue}</div>}
    </div>
  );

  const renderBaseBlock = (titleLabel, aggData, mcData, themeColor) => {
    if (!aggData || Object.keys(aggData).length === 0) return null;
    
    const reversedIntervals = [...intervals].reverse();
    
    return (
      <div style={{ ...baseStyles.card, padding: '20px', border: `1px solid ${themeColor}80`, background: 'linear-gradient(180deg, #0f172a 0%, #020617 100%)', marginTop: '20px' }}>
        <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <strong style={{ fontSize: '15px', color: themeColor, fontWeight: '900', display: 'flex', alignItems: 'center', gap: '6px' }}>
            ⚡ BẢNG SOI ĐỒNG BỘ ĐA KHUNG THỜI GIAN ({titleLabel})
          </strong>
          <span style={{ fontSize: '12px', color: '#ffffff', fontWeight: '800' }}>Cập nhật Market Context: OHLC, Giá, CVD, OI & Funding</span>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px' }}>
          {intervals.map((inv) => {
            const candle = aggData[inv]?.[0]; 
            if (!candle) return null;
            
            const isSelected = selectedCandle?.id === candle.id;
            const stateInfo = ALGO_DICT[candle.state] || { color: '#64748b', short: 'UNKN' };
            
            const ctx = mcData.intervals?.[inv] || {};
            const oldOI = ctx.oldOI;
            const currentOI = mcData.currentOI;
            const oiDelta = (currentOI && oldOI) ? currentOI - oldOI : 0;
            const oiDeltaPct = oldOI ? (oiDelta / oldOI) * 100 : 0;
            
            const cvd = candle.details.takerBuyQuoteVol - candle.details.takerSellQuoteVol;
            const isCvdPos = cvd >= 0;
            const isOiPos = oiDelta >= 0;
            
            const fundRate = ctx.funding !== undefined && ctx.funding !== null ? (ctx.funding * 100).toFixed(4) : 'N/A';
            const volPerHour = candle.details.quoteVolume / inv;

            return (
              <div
                key={`agg-box-${titleLabel}-${inv}`}
                onClick={() => setSelectedCandle(candle)}
                style={{
                  background: isSelected ? 'rgba(255,255,255,0.04)' : '#1e293b80',
                  border: isSelected ? `2px solid #ffffff` : `1px solid #475569`,
                  boxShadow: isSelected ? `0 0 15px ${stateInfo.color}60` : 'none',
                  borderRadius: '8px', padding: '14px', cursor: 'pointer', transition: 'all 0.2s ease',
                  display: 'flex', flexDirection: 'column', gap: '10px', position: 'relative'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '16px', fontWeight: '900', color: '#ffffff' }}>{inv}H KHUNG</span>
                  <span style={{ fontSize: '13px', fontWeight: '900', color: candle.candleColor }}>{candle.spreadStr}</span>
                </div>

                <div style={{ fontSize: '13px', fontWeight: '900', color: stateInfo.color, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {candle.state.replace(/_/g, ' ')} ({stateInfo.short})
                </div>

                <div style={{ width: '100%', height: '5px', backgroundColor: '#334155', borderRadius: '2px', overflow: 'hidden', display: 'flex', border: '1px solid #1e293b' }}>
                  <div style={{ width: `${candle.details.takerBuy}%`, backgroundColor: colors.green }}></div>
                  <div style={{ width: `${candle.details.takerSell}%`, backgroundColor: colors.red }}></div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', fontSize: '11px', marginTop: '4px', padding: '8px', backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.05)' }}>
                   <div style={{ color: colors.textSub, display: 'flex', flexDirection: 'column' }}>
                     <span style={{fontSize: '9px', textTransform:'uppercase'}}>Net CVD</span>
                     <strong style={{ color: isCvdPos ? colors.green : colors.red }}>{isCvdPos?'+':''}{formatUSD(cvd)}</strong>
                   </div>
                   <div style={{ color: colors.textSub, display: 'flex', flexDirection: 'column' }}>
                     <span style={{fontSize: '9px', textTransform:'uppercase'}}>OI Delta ({inv}h)</span>
                     <strong style={{ color: isOiPos ? colors.green : colors.red }}>{isOiPos?'+':''}{oiDeltaPct.toFixed(2)}%</strong>
                   </div>
                   <div style={{ color: colors.textSub, display: 'flex', flexDirection: 'column' }}>
                     <span style={{fontSize: '9px', textTransform:'uppercase'}}>Funding Rate</span>
                     <strong style={{ color: fundRate > 0 ? colors.green : (fundRate < 0 ? colors.red : '#fff') }}>{fundRate}%</strong>
                   </div>
                   <div style={{ color: colors.textSub, display: 'flex', flexDirection: 'column' }}>
                     <span style={{fontSize: '9px', textTransform:'uppercase'}}>Volume/Giờ</span>
                     <strong style={{ color: '#fff' }}>{formatUSD(volPerHour)}</strong>
                   </div>
                </div>

                <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '800', marginTop: 'auto', fontFamily: 'monospace' }}>
                  Bắt đầu tính từ: {candle.realTime.split(',')[1] || candle.realTime}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: '20px', padding: '16px', backgroundColor: 'rgba(0,0,0,0.4)', borderRadius: '8px', border: `1px solid ${colors.border}` }}>
           <h4 style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#fbbf24', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Database size={16} /> BẢNG DỮ LIỆU CHUỖI ĐỘNG LƯỢNG & DÒNG TIỀN 
           </h4>
           {/* KHỐI TỔNG KẾT XU HƯỚNG TRỰC QUAN (MŨI TÊN) */}
              <div style={{ marginTop: '20px', padding: '16px', backgroundColor: 'rgba(0,0,0,0.4)', borderRadius: '8px', border: `1px solid ${colors.border}` }}>
                 <h4 style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#fbbf24', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <TrendingUp size={16} /> TỔNG KẾT LUỒNG XU HƯỚNG THEO THỜI GIAN (12H ➔ 1H) - {titleLabel}
                 </h4>
                 
                 <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                   <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                     <div style={{ width: '80px', fontSize: '12px', fontWeight: 'bold', color: colors.textSub }}>Chuỗi Nến:</div>
                     <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                       {reversedIntervals.map((inv, idx) => {
                         const c = aggData[inv]?.[0];
                         if(!c) return null;
                         return (
                           <React.Fragment key={`trend-p-${inv}`}>
                             <span style={{ color: c.isGreen ? colors.green : colors.red, fontWeight: 'bold', fontSize: '13px' }}>{inv}H {c.isGreen ? '▲' : '▼'}</span>
                             {idx < reversedIntervals.length - 1 && <span style={{ color: '#475569' }}>➔</span>}
                           </React.Fragment>
                         );
                       })}
                     </div>
                   </div>
      
                   <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                     <div style={{ width: '80px', fontSize: '12px', fontWeight: 'bold', color: colors.textSub }}>Lực CVD:</div>
                     <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                       {reversedIntervals.map((inv, idx) => {
                         const c = aggData[inv]?.[0];
                         if(!c) return null;
                         const cvd = c.details.takerBuyQuoteVol - c.details.takerSellQuoteVol;
                         return (
                           <React.Fragment key={`trend-cvd-${inv}`}>
                             <span style={{ color: cvd >= 0 ? colors.green : colors.red, fontWeight: 'bold', fontSize: '13px' }}>{inv}H {cvd >= 0 ? '+' : '-'}</span>
                             {idx < reversedIntervals.length - 1 && <span style={{ color: '#475569' }}>➔</span>}
                           </React.Fragment>
                         );
                       })}
                     </div>
                   </div>
      
                   <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                     <div style={{ width: '80px', fontSize: '12px', fontWeight: 'bold', color: colors.textSub }}>Luồng OI:</div>
                     <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                       {reversedIntervals.map((inv, idx) => {
                         const ctx = mcData.intervals?.[inv] || {};
                         const delta = (mcData.currentOI && ctx.oldOI) ? (mcData.currentOI - ctx.oldOI) : 0;
                         return (
                           <React.Fragment key={`trend-oi-${inv}`}>
                             <span style={{ color: delta >= 0 ? colors.green : colors.red, fontWeight: 'bold', fontSize: '13px' }}>{inv}H {delta >= 0 ? 'Tăng' : 'Giảm'}</span>
                             {idx < reversedIntervals.length - 1 && <span style={{ color: '#475569' }}>➔</span>}
                           </React.Fragment>
                         );
                       })}
                     </div>
                   </div>
                 </div>
              </div>

           {/* PHẦN 1: BÓC TÁCH OHLC VĨ MÔ & VI MÔ VỚI SO SÁNH ĐỘNG CỠ LỚN */}
           {aggData[12] && aggData[1] && (() => {
             const d12 = aggData[12][0].details;
             const d1 = aggData[1][0].details;
             
             const o12 = parseFloat(d12.open);
             const o1 = parseFloat(d1.open);
             const h12 = parseFloat(d12.high);
             const h1 = parseFloat(d1.high);
             const l12 = parseFloat(d12.low);
             const l1 = parseFloat(d1.low);
             const c12 = parseFloat(d12.close);
             const c1 = parseFloat(d1.close);

             const getCol = (v1, v2) => v1 > v2 ? colors.green : (v1 < v2 ? colors.red : '#fff');

             return (
               <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <strong style={{fontSize:'13px', color: colors.blue, display:'block', marginBottom:'12px'}}>1. ĐỐI CHIẾU VỊ THẾ GIÁ (Vĩ mô 12H vs Vi mô 1H)</strong>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                     <div style={{ padding: '12px', backgroundColor: 'rgba(0,0,0,0.4)', borderRadius: '6px' }}>
                       <div style={{fontSize:'11px', color:colors.textSub, marginBottom:'8px', fontWeight:'bold'}}>KHUNG 12 GIỜ QUA (Mức cản lớn)</div>
                       <div style={{display:'flex', justifyContent:'space-between', fontSize:'13px', fontFamily:'monospace'}}>
                         <span style={{color:colors.textSub}}>O: <strong style={{color: getCol(o12, o1)}}>{d12.open}</strong></span>
                         <span style={{color:colors.textSub}}>H: <strong style={{color: getCol(h12, h1)}}>{d12.high}</strong></span>
                         <span style={{color:colors.textSub}}>L: <strong style={{color: getCol(l12, l1)}}>{d12.low}</strong></span>
                         <span style={{color:colors.textSub}}>C: <strong style={{color: getCol(c12, c1)}}>{d12.close}</strong></span>
                       </div>
                     </div>
                     <div style={{ padding: '12px', backgroundColor: 'rgba(0,0,0,0.4)', borderRadius: '6px' }}>
                       <div style={{fontSize:'11px', color:colors.textSub, marginBottom:'8px', fontWeight:'bold'}}>KHUNG 1 GIỜ QUA (Động lượng hiện tại)</div>
                       <div style={{display:'flex', justifyContent:'space-between', fontSize:'13px', fontFamily:'monospace'}}>
                         <span style={{color:colors.textSub}}>O: <strong style={{color: getCol(o1, o12)}}>{d1.open}</strong></span>
                         <span style={{color:colors.textSub}}>H: <strong style={{color: getCol(h1, h12)}}>{d1.high}</strong></span>
                         <span style={{color:colors.textSub}}>L: <strong style={{color: getCol(l1, l12)}}>{d1.low}</strong></span>
                         <span style={{color:colors.textSub}}>C: <strong style={{color: getCol(c1, c12)}}>{d1.close}</strong></span>
                       </div>
                     </div>
                  </div>
               </div>
             );
           })()}

           {/* PHẦN 2: CHUỖI DỮ LIỆU ĐỘNG */}
           <div style={{ overflowX: 'auto', backgroundColor: 'rgba(0,0,0,0.2)', padding: '16px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.05)' }}>
              <strong style={{fontSize:'13px', color: colors.blue, display:'block', marginBottom:'12px'}}>2. THEO DÕI ĐỘT BIẾN THEO CHUỖI THỜI GIAN (Từ 12H lùi về Hiện tại)</strong>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left', whiteSpace: 'nowrap' }}>
                 <thead>
                   <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                     <th style={{ padding: '10px 8px', color: colors.textSub, fontWeight:'bold' }}>CHỈ SỐ (Trailing)</th>
                     {reversedIntervals.map(inv => (
                       <th key={`th-${inv}`} style={{ padding: '10px 8px', color: '#fff', fontWeight:'bold' }}>{inv}H TRƯỚC</th>
                     ))}
                     <th style={{ padding: '10px 8px', color: colors.warn, fontWeight:'bold' }}>HIỆN TẠI (NOW)</th>
                   </tr>
                 </thead>
                 <tbody>
                   <tr style={{ borderBottom: `1px dashed rgba(255,255,255,0.05)` }}>
                     <td style={{ padding: '12px 8px', color: '#e2e8f0', fontWeight:'bold' }}>Mức Giá (Open/Close)</td>
                     {reversedIntervals.map(inv => (
                       <td key={`td-p-${inv}`} style={{ padding: '12px 8px', fontFamily:'monospace', color: aggData[inv]?.[0]?.details.open > aggData[1]?.[0]?.details.close ? colors.green : colors.red }}>
                         {aggData[inv]?.[0]?.details.open || '-'}
                       </td>
                     ))}
                     <td style={{ padding: '12px 8px', fontFamily:'monospace', color: colors.warn, fontWeight:'bold' }}>
                       {aggData[1]?.[0]?.details.close || '-'}
                     </td>
                   </tr>

                   <tr style={{ borderBottom: `1px dashed rgba(255,255,255,0.05)` }}>
                     <td style={{ padding: '12px 8px', color: '#e2e8f0', fontWeight:'bold' }}>Tốc độ Vol / Giờ</td>
                     {reversedIntervals.map(inv => {
                       const c = aggData[inv]?.[0];
                       const volH = c ? (c.details.quoteVolume / inv) : 0;
                       return <td key={`td-vh-${inv}`} style={{ padding: '12px 8px', fontFamily:'monospace', color: '#e2e8f0' }}>
                         {volH ? formatUSD(volH) : '-'}
                       </td>
                     })}
                     <td style={{ padding: '12px 8px', color: colors.textSub }}>-</td>
                   </tr>

                   <tr style={{ borderBottom: `1px dashed rgba(255,255,255,0.05)` }}>
                     <td style={{ padding: '12px 8px', color: '#e2e8f0', fontWeight:'bold' }}>Tổng Net CVD</td>
                     {reversedIntervals.map(inv => {
                       const c = aggData[inv]?.[0];
                       const cvd = c ? (c.details.takerBuyQuoteVol - c.details.takerSellQuoteVol) : 0;
                       return <td key={`td-cvd-${inv}`} style={{ padding: '12px 8px', fontFamily:'monospace', color: cvd > 0 ? colors.green : (cvd < 0 ? colors.red : '#e2e8f0') }}>
                         {cvd ? (cvd > 0 ? '+' : '') + formatUSD(cvd) : '-'}
                       </td>
                     })}
                     <td style={{ padding: '12px 8px', color: colors.textSub }}>-</td>
                   </tr>

                   <tr style={{ borderBottom: `1px dashed rgba(255,255,255,0.05)` }}>
                     <td style={{ padding: '12px 8px', color: '#e2e8f0', fontWeight:'bold' }}>Hợp đồng mở (OI)</td>
                     {reversedIntervals.map(inv => {
                       const oi = mcData.intervals?.[inv]?.oldOI;
                       return <td key={`td-oi-${inv}`} style={{ padding: '12px 8px', fontFamily:'monospace', color: oi > mcData.currentOI ? colors.green : colors.red }}>
                         {oi ? oi.toLocaleString(undefined, {maximumFractionDigits: 2}) : '-'}
                       </td>
                     })}
                     <td style={{ padding: '12px 8px', fontFamily:'monospace', color: colors.warn, fontWeight:'bold' }}>
                       {mcData.currentOI ? mcData.currentOI.toLocaleString(undefined, {maximumFractionDigits: 2}) : '-'}
                     </td>
                   </tr>

                   <tr>
                     <td style={{ padding: '12px 8px', color: '#e2e8f0', fontWeight:'bold' }}>Funding Rate</td>
                     {reversedIntervals.map(inv => {
                       const fr = mcData.intervals?.[inv]?.funding;
                       return <td key={`td-fr-${inv}`} style={{ padding: '12px 8px', fontFamily:'monospace', color: fr > 0 ? colors.green : (fr < 0 ? colors.red : '#94a3b8') }}>
                         {fr !== null && fr !== undefined ? (fr * 100).toFixed(4) + '%' : '-'}
                       </td>
                     })}
                     <td style={{ padding: '12px 8px', color: colors.textSub }}>-</td>
                   </tr>
                 </tbody>
              </table>
              
              <div style={{fontSize:'11px', color:colors.textSub, marginTop:'16px', fontStyle:'italic', lineHeight:'1.5'}}>
                * Ghi chú: "Tốc độ Vol / Giờ" và "Tổng Net CVD" là dữ liệu cộng gộp chia trung bình để bạn dễ so sánh động lượng. "OI" và "Funding Rate" là điểm dữ liệu Snapshot lưu lại tại thời điểm bắt đầu mở nến để theo dấu hành vi nhồi lệnh.
              </div>
           </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ backgroundColor: colors.bg || '#0b0e14', color: '#ffffff', minHeight: '150px', padding: '20px', boxSizing: 'border-box' }}>
      <div style={{ maxWidth: '1600px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        
        <div style={{ ...baseStyles.card, display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Layers size={24} color={colors.blue || '#3b82f6'} />
              <div>
                <h1 style={{ margin: 0, fontSize: '20px', color: '#ffffff', fontWeight: 'bold' }}>MTF RIBBON MATRIX</h1>
                <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#ffffff', fontWeight: '700' }}>Đồng bộ cấu trúc Vi mô & Vĩ mô đa khung</p>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input 
                  type="text" 
                  value={inputSymbol} 
                  onChange={(e) => setInputSymbol(e.target.value)} 
                  placeholder="Ví dụ: BTCUSDT"
                  style={{ ...baseStyles.input, width: '120px', textTransform: 'uppercase', color: '#ffffff', fontWeight: 'bold' }} 
                  disabled={isLoading}
                />
                
                <button 
                  onClick={() => handleExecuteAggregate('5m')} 
                  disabled={isLoading} 
                  style={{ ...baseStyles.select, backgroundColor: isLoading ? colors.border : '#65a30d', color: '#ffffff', border: 'none', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  {isLoading ? 'XỬ LÝ...' : 'Quy đổi (5M Base)'}
                </button>

                <button 
                  onClick={() => handleExecuteAggregate('15m')} 
                  disabled={isLoading} 
                  style={{ ...baseStyles.select, backgroundColor: isLoading ? colors.border : '#059669', color: '#ffffff', border: 'none', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  {isLoading ? 'XỬ LÝ...' : 'Quy đổi (15M Base)'}
                </button>

                <button 
                  onClick={handleSearch} 
                  disabled={isLoading} 
                  style={{ ...baseStyles.select, backgroundColor: isLoading ? colors.border : (colors.blue || '#3b82f6'), color: '#ffffff', border: 'none', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  TÌM QUÁ KHỨ
                </button>
                <button onClick={handleOpenSettings} style={{ ...baseStyles.select, padding: '8px 12px', backgroundColor: 'rgba(255,255,255,0.05)', color: '#ffffff', border: `1px solid ${colors.border}`, cursor: 'pointer', fontWeight: 'bold' }}>
                  <Settings size={16} /> CONFIG ENGINE
                </button>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: 'rgba(255,255,255,0.05)', padding: '4px', borderRadius: '8px', border: `1px solid ${colors.border}` }}>
                <span style={{ fontSize: '12px', color: '#ffffff', padding: '0 8px', fontWeight: 'bold' }}>TẦM NHÌN:</span>
                {[1, 3, 5].map(days => (
                  <button 
                    key={days} 
                    onClick={() => handleLookbackChange(days)}
                    style={{
                      backgroundColor: lookback === days ? (colors.blue || '#3b82f6') : 'transparent',
                      color: '#ffffff',
                      border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px', transition: 'all 0.2s'
                    }}
                  >
                    {days} NGÀY
                  </button>
                ))}
              </div>
            </div>
          </div>

          {showSettings && (
            <div style={{ backgroundColor: 'rgba(0,0,0,0.5)', border: `1px dashed ${colors.blue}`, padding: '20px', borderRadius: '8px', marginTop: '8px' }}>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', borderBottom: `1px solid ${colors.border}`, paddingBottom: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <strong style={{ fontSize: '15px', color: colors.blue }}>TÙY CHỈNH THUẬT TOÁN PRICE ACTION</strong>
                  <span style={{ fontSize: '11px', color: colors.textSub, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Info size={12}/> Tùy chỉnh các tỷ lệ hình thái nến để tự động tính toán ra 24 trạng thái vi mô.
                  </span>
                </div>
                
                <div style={{ display: 'flex', gap: '12px', flexShrink: 0 }}>
                  <button onClick={handleCancelSettings} style={{ backgroundColor: 'transparent', color: colors.textSub, border: `1px solid ${colors.border}`, padding: '8px 16px', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <X size={14} /> HỦY BỎ
                  </button>
                  <button onClick={handleApplyConfig} style={{ backgroundColor: colors.green, color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Check size={14} /> CẬP NHẬT & QUÉT LẠI
                  </button>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '12px', color: colors.textMain, fontWeight: 'bold' }}>Thân nến cực đại (Marubozu) &ge; {draftConfig.marubozuBodyRatio}</label>
                  <input type="number" step="0.05" min="0.5" max="1.0" value={draftConfig.marubozuBodyRatio} onChange={(e) => setDraftConfig({...draftConfig, marubozuBodyRatio: e.target.value})} style={baseStyles.input} />
                  <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', fontStyle: 'italic', lineHeight: '1.4' }}>
                    <span style={{color: '#38bdf8', fontWeight: 'bold'}}>Khuyên dùng: 0.85 - 0.95</span><br/>Dùng để bắt những cây nến cường lực full-body (DUMP/PUMP).
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '12px', color: colors.textMain, fontWeight: 'bold' }}>Thân nến mạnh (S-Bull/S-Bear) &ge; {draftConfig.strongBodyRatio}</label>
                  <input type="number" step="0.05" min="0.3" max="0.9" value={draftConfig.strongBodyRatio} onChange={(e) => setDraftConfig({...draftConfig, strongBodyRatio: e.target.value})} style={baseStyles.input} />
                  <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', fontStyle: 'italic', lineHeight: '1.4' }}>
                    <span style={{color: '#38bdf8', fontWeight: 'bold'}}>Khuyên dùng: 0.5 - 0.7</span><br/>Tỷ lệ thân nến chiếm ưu thế cho thấy có sự kiểm soát rõ ràng.
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '12px', color: colors.textMain, fontWeight: 'bold' }}>Tỷ lệ Râu từ chối (Hammer/Star) &ge; {draftConfig.stopHuntWickRatio}</label>
                  <input type="number" step="0.05" min="0.2" max="0.8" value={draftConfig.stopHuntWickRatio} onChange={(e) => setDraftConfig({...draftConfig, stopHuntWickRatio: e.target.value})} style={baseStyles.input} />
                  <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', fontStyle: 'italic', lineHeight: '1.4' }}>
                    <span style={{color: '#38bdf8', fontWeight: 'bold'}}>Khuyên dùng: 0.45 - 0.65</span><br/>Râu phải đủ dài so với toàn bộ nến để được gọi là Pinbar xịn.
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '12px', color: colors.textMain, fontWeight: 'bold' }}>Biên độ tối đa Doji &le; {draftConfig.dojiBodyMax}</label>
                  <input type="number" step="0.05" min="0.01" max="0.3" value={draftConfig.dojiBodyMax} onChange={(e) => setDraftConfig({...draftConfig, dojiBodyMax: e.target.value})} style={baseStyles.input} />
                  <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', fontStyle: 'italic', lineHeight: '1.4' }}>
                    <span style={{color: '#38bdf8', fontWeight: 'bold'}}>Khuyên dùng: 0.05 - 0.15</span><br/>Thân nến phải siêu hẹp mới thể hiện được sự giằng co lưỡng lự.
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '12px', color: colors.textMain, fontWeight: 'bold' }}>Hệ số Biến động Volatility &ge; {draftConfig.volatileMultiplier}</label>
                  <input type="number" step="0.1" min="1.0" max="3.0" value={draftConfig.volatileMultiplier} onChange={(e) => setDraftConfig({...draftConfig, volatileMultiplier: e.target.value})} style={baseStyles.input} />
                  <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', fontStyle: 'italic', lineHeight: '1.4' }}>
                    <span style={{color: '#38bdf8', fontWeight: 'bold'}}>Khuyên dùng: 1.2 - 2.0</span><br/>Biên độ Spread gấp bao nhiêu lần mức trung bình (ATR) để gọi là đột biến.
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '12px', color: colors.textMain, fontWeight: 'bold' }}>Vol cạn kiệt (Exhausted) &lt; {draftConfig.exhaustedVolMax}</label>
                  <input type="number" step="0.1" min="0.1" max="2.0" value={draftConfig.exhaustedVolMax} onChange={(e) => setDraftConfig({...draftConfig, exhaustedVolMax: e.target.value})} style={baseStyles.input} />
                  <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', fontStyle: 'italic', lineHeight: '1.4' }}>
                    <span style={{color: '#38bdf8', fontWeight: 'bold'}}>Khuyên dùng: 0.5 - 0.9</span><br/>Khối lượng giao dịch sụt giảm so với trung bình, thị trường hết xăng.
                  </div>
                </div>
                {/* 3 Block mới thêm vào UI */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '12px', color: colors.textMain, fontWeight: 'bold' }}>Thân nến yếu (W-Bull/Bear) &ge; {draftConfig.weakBodyRatio}</label>
                  <input type="number" step="0.05" min="0.1" max="0.5" value={draftConfig.weakBodyRatio} onChange={(e) => setDraftConfig({...draftConfig, weakBodyRatio: e.target.value})} style={baseStyles.input} />
                  <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', fontStyle: 'italic', lineHeight: '1.4' }}>
                    <span style={{color: '#38bdf8', fontWeight: 'bold'}}>Khuyên dùng: 0.2 - 0.4</span><br/>Tỷ lệ thân nến tối thiểu để ghi nhận có xu hướng nhỏ.
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '12px', color: colors.textMain, fontWeight: 'bold' }}>% Taker Volume (Pump/Dump) &gt; {draftConfig.pumpDumpTakerPct}%</label>
                  <input type="number" step="1" min="50" max="95" value={draftConfig.pumpDumpTakerPct} onChange={(e) => setDraftConfig({...draftConfig, pumpDumpTakerPct: e.target.value})} style={baseStyles.input} />
                  <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', fontStyle: 'italic', lineHeight: '1.4' }}>
                    <span style={{color: '#38bdf8', fontWeight: 'bold'}}>Khuyên dùng: 60 - 75</span><br/>Phần trăm volume chủ động (Taker) bắt buộc phải đạt để gọi là Bơm/Xả.
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '12px', color: colors.textMain, fontWeight: 'bold' }}>Hệ số nén giá (Compression) &le; {draftConfig.compressionMultiplier}</label>
                  <input type="number" step="0.1" min="0.2" max="0.9" value={draftConfig.compressionMultiplier} onChange={(e) => setDraftConfig({...draftConfig, compressionMultiplier: e.target.value})} style={baseStyles.input} />
                  <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', fontStyle: 'italic', lineHeight: '1.4' }}>
                    <span style={{color: '#38bdf8', fontWeight: 'bold'}}>Khuyên dùng: 0.4 - 0.6</span><br/>Biên độ nến nhỏ hơn bao nhiêu lần so với ATR để xác nhận sideway/tích lũy.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px' }}>
          <div style={{ ...baseStyles.card, display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${colors.border}`, paddingBottom: '12px' }}>
              <strong style={{ color: colors.warn, fontSize: '14px', fontWeight: 'bold' }}>📊 PHÂN LOẠI TRẠNG THÁI HỆ THỐNG</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${colors.border}`, paddingBottom: '12px' }}>
              <strong style={{ color: colors.warn, fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Activity size={16} /> TỔNG KẾT ({baseStats.total} NẾN 1H / {displayLookback} NGÀY)
              </strong>
              <div style={{ display: 'flex', gap: '12px', fontSize: '12px', fontWeight: 'bold' }}>
                <span style={{ color: colors.green, backgroundColor: 'rgba(34, 197, 94, 0.1)', padding: '4px 8px', borderRadius: '4px', border: `1px solid rgba(34, 197, 94, 0.2)` }}>NẾN TĂNG: {baseStats.green}</span>
                <span style={{ color: colors.red, backgroundColor: 'rgba(239, 68, 68, 0.1)', padding: '4px 8px', borderRadius: '4px', border: `1px solid rgba(239, 68, 68, 0.2)` }}>NẾN GIẢM: {baseStats.red}</span>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {CATEGORY_GROUPS.map(group => (
                <div key={group.id}>
                  <h3 style={{ fontSize: '12px', color: '#ffffff', fontWeight: '900', marginBottom: '8px', borderLeft: '3px solid #3b82f6', paddingLeft: '6px' }}>{group.name}</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '8px' }}>
                    {group.keys.map(key => {
                      const info = ALGO_DICT[key];
                      if (!info) return null;
                      return (
                        <div key={key} style={{ backgroundColor: 'rgba(0,0,0,0.4)', border: `1px solid ${colors.border}`, padding: '8px 10px', borderRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <div style={{ width: '8px', height: '8px', borderRadius: '2px', backgroundColor: info.color }}></div>
                            <span style={{ fontSize: '11px', color: '#ffffff', fontWeight: '800' }}>{info.short}</span>
                          </div>
                          <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#ffffff' }}>{baseStats.states[key] || 0}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {selectedCandle && (
            <div style={{ ...baseStyles.card, display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '200px', height: '200px', filter: 'blur(80px)', borderRadius: '50%', opacity: 0.15, backgroundColor: ALGO_DICT[selectedCandle.state].color, pointerEvents: 'none' }}></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: `1px solid ${colors.border}`, paddingBottom: '12px', marginBottom: '16px', position: 'relative', zIndex: 10 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <strong style={{ color: colors.textMain, fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <BarChart2 size={16} color={colors.blue}/> NẾN ĐANG SOI: 
                    <span style={{ backgroundColor: 'rgba(59, 130, 246, 0.2)', color: colors.blue, padding: '2px 6px', borderRadius: '4px', fontSize: '12px' }}>[{selectedCandle.interval}]</span>
                  </strong>
                  <div style={{ display: 'flex', gap: '16px', fontSize: '12px', fontFamily: 'monospace', backgroundColor: 'rgba(0,0,0,0.3)', padding: '6px 12px', borderRadius: '6px', border: `1px solid rgba(255,255,255,0.05)` }}>
                    <span style={{color: colors.textSub}}>O: <span style={{color: '#fff', fontWeight: 'bold'}}>{selectedCandle.details.open}</span></span>
                    <span style={{color: colors.textSub}}>H: <span style={{color: colors.green, fontWeight: 'bold'}}>{selectedCandle.details.high}</span></span>
                    <span style={{color: colors.textSub}}>L: <span style={{color: colors.red, fontWeight: 'bold'}}>{selectedCandle.details.low}</span></span>
                    <span style={{color: colors.textSub}}>C: <span style={{color: selectedCandle.isGreen ? colors.green : colors.red, fontWeight: 'bold'}}>{selectedCandle.details.close}</span></span>
                  </div>
                </div>
                <span style={{ fontSize: '12px', color: colors.textSub, display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}><Clock size={12}/> {selectedCandle.realTime}</span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', position: 'relative', zIndex: 10 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: ALGO_DICT[selectedCandle.state].color, letterSpacing: '0.5px' }}>
                      {selectedCandle.state.replace('_', ' ')}
                    </div>
                    <div style={{ fontSize: '12px', color: colors.textSub, backgroundColor: 'rgba(0,0,0,0.3)', padding: '10px', borderRadius: '6px', border: `1px solid ${colors.border}`, lineHeight: '1.4' }}>
                      {ALGO_DICT[selectedCandle.state].desc}
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', fontWeight: 'bold', marginBottom: '6px' }}>
                        <span style={{ color: colors.green }}>MUA CĐ ({selectedCandle.details.takerBuy}%) {formatUSD(selectedCandle.details.takerBuyQuoteVol)}</span>
                        <span style={{ color: colors.red }}>BÁN CĐ ({selectedCandle.details.takerSell}%) {formatUSD(selectedCandle.details.takerSellQuoteVol)}</span>
                      </div>
                      <div style={{ width: '100%', height: '12px', backgroundColor: 'rgba(0,0,0,0.5)', borderRadius: '6px', display: 'flex', overflow: 'hidden', border: `1px solid ${colors.border}` }}>
                        <div style={{ width: `${selectedCandle.details.takerBuy}%`, backgroundColor: colors.green, transition: 'width 0.3s ease-out' }}></div>
                        <div style={{ width: `${selectedCandle.details.takerSell}%`, backgroundColor: colors.red, transition: 'width 0.3s ease-out' }}></div>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: '14px', paddingBottom: '14px' }}>
                  <div style={{ fontSize: '10px', fontWeight: '700', color: colors.textSub, letterSpacing: '1px', marginBottom: '10px', textTransform: 'uppercase' }}>
                    📊 Thông tin nến soi
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '8px' }}>
                    {statBox('Màu nến', selectedCandle.isGreen ? 'TĂNG ▲' : 'GIẢM ▼', selectedCandle.candleColor)}
                    {statBox('Biên độ (Spread)', selectedCandle.details.spread, selectedCandle.isGreen ? colors.green : colors.red)}
                    {statBox('Râu nến', selectedCandle.details.wick, '#fff')}
                    {statBox('% Range (H-L)', `${selectedCandle.details.rangePct}%`, '#facc15', '(High - Low) / Low')}
                  </div>

                  {(() => {
                    const d = selectedCandle.details;
                    const coinLabel = symbol.replace('USDT','').replace('BUSD','').replace('FDUSD','');
                    const fmtUsdt = (val) => {
                      if (val >= 1000000) return `$${(val/1000000).toFixed(2)}M`;
                      if (val >= 1000) return `$${(val/1000).toFixed(2)}K`;
                      return `$${val.toFixed(2)}`;
                    };
                    const fmtCoin = (val) => {
                      if (val >= 1000) return val.toFixed(2);
                      if (val >= 1) return val.toFixed(4);
                      return val.toFixed(6);
                    };
                    return (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                        <div style={{ backgroundColor: 'rgba(0,0,0,0.4)', border: `1px solid ${colors.border}`, padding: '10px 12px', borderRadius: '6px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <div style={{ fontSize: '9px', color: colors.textSub, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Số giao dịch</div>
                          <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#a78bfa' }}>{d.trades.toLocaleString()}</div>
                          <div style={{ fontSize: '9px', color: colors.textSub }}>Number of Trades</div>
                        </div>
                        <div style={{ backgroundColor: 'rgba(0,0,0,0.4)', border: `1px solid ${colors.border}`, padding: '10px 12px', borderRadius: '6px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <div style={{ fontSize: '9px', color: colors.textSub, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Vol / Trade</div>
                          <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#38bdf8' }}>{fmtCoin(d.avgVolCoin)} <span style={{ fontSize: '9px', color: colors.textSub }}>{coinLabel}</span></div>
                          <div style={{ fontSize: '11px', fontWeight: '600', color: '#7dd3fc' }}>{fmtUsdt(d.avgVolCoin * d.close)}</div>
                          <div style={{ fontSize: '9px', color: colors.textSub }}>Volume ÷ Trades</div>
                        </div>
                        <div style={{ backgroundColor: 'rgba(0,0,0,0.4)', border: `1px solid ${colors.border}`, padding: '10px 12px', borderRadius: '6px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <div style={{ fontSize: '9px', color: colors.textSub, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Buy / Trade</div>
                          <div style={{ fontSize: '12px', fontWeight: 'bold', color: colors.green }}>{fmtCoin(d.avgBuyCoin)} <span style={{ fontSize: '9px', color: colors.textSub }}>{coinLabel}</span></div>
                          <div style={{ fontSize: '11px', fontWeight: '600', color: '#86efac' }}>{fmtUsdt(d.avgBuyCoin * d.close)}</div>
                          <div style={{ fontSize: '9px', color: colors.textSub }}>Taker Buy ÷ Trades</div>
                        </div>
                        <div style={{ backgroundColor: 'rgba(0,0,0,0.4)', border: `1px solid ${colors.border}`, padding: '10px 12px', borderRadius: '6px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <div style={{ fontSize: '9px', color: colors.textSub, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Sell / Trade</div>
                          <div style={{ fontSize: '12px', fontWeight: 'bold', color: colors.red }}>{fmtCoin(d.avgSellCoin)} <span style={{ fontSize: '9px', color: colors.textSub }}>{coinLabel}</span></div>
                          <div style={{ fontSize: '11px', fontWeight: '600', color: '#fca5a5' }}>{fmtUsdt(d.avgSellCoin * d.close)}</div>
                          <div style={{ fontSize: '9px', color: colors.textSub }}>Taker Sell ÷ Trades</div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
              <CandleAnalyzer selectedCandle={selectedCandle} />
            </div>
          )}
        </div>

        {Object.keys(matrixData).length > 0 && (
          <div style={{ ...baseStyles.card, padding: '20px' }}>
            <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong style={{ fontSize: '14px', color: '#ffffff', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}><AlignLeft size={16} color={colors.blue}/> MA TRẬN DẢI BĂNG QUÁ KHỨ (RIBBON MATRIX LỊCH SỬ)</strong>
              <span style={{ fontSize: '12px', color: '#ffffff', fontWeight: '700' }}>Bấm vào bất kỳ ô nào để xem chi tiết bên trên</span>
            </div>
            <div className="matrix-scroll-container" style={{ overflowX: 'auto', paddingBottom: '10px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 'max-content' }}>
                {intervals.map((inv) => {
                  const totalSlots = matrixData[1]?.length || 0;
                  const cells = [];
                  for (let i = 0; i < totalSlots; i += inv) {
                    const candleIndex = Math.floor(i / inv);
                    const candle = matrixData[inv]?.[candleIndex];
                    if (candle) cells.push({ ...candle, flexValue: inv });
                  }
                  return (
                    <div key={`row-raw-${inv}`} style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
                      <div style={{ width: '48px', flexShrink: 0, backgroundColor: 'rgba(0,0,0,0.6)', border: '1px solid #334155', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '900', color: '#ffffff' }}>{inv}H</div>
                      <div style={{ display: 'flex', flex: 1, gap: '4px' }}>
                        {cells.map((candle) => {
                          const isSelected = selectedCandle?.id === candle.id;
                          const stateInfo = ALGO_DICT[candle.state] || { color: '#64748b', short: 'UNKN' };
                          return (
                            <div 
                              key={candle.id} onClick={() => setSelectedCandle(candle)}
                              style={{
                                flex: candle.flexValue, minWidth: `${candle.flexValue * 10}px`, height: '52px',
                                backgroundColor: isSelected ? `${stateInfo.color}` : `${stateInfo.color}85`,
                                border: isSelected ? `2px solid #ffffff` : `1px solid ${stateInfo.color}40`,
                                boxShadow: isSelected ? `0 0 12px ${stateInfo.color}` : 'none',
                                borderRadius: '4px', cursor: 'pointer', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '4px', boxSizing: 'border-box'
                              }}
                              title={`[${candle.interval}] ${candle.time} - ${candle.state}`}
                            >
                              <div style={{ fontSize: '11px', fontWeight: '900', color: candle.candleColor, width: '100%', textAlign: 'center' }}>
                                {displayLookback === 1 || inv >= 4 ? candle.spreadStr : '•'}
                              </div>
                              <div style={{ fontSize: '10px', fontWeight: '900', color: '#ffffff', width: '100%', textAlign: 'center', marginTop: 'auto', textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}>
                                {displayLookback <= 3 || inv >= 6 ? stateInfo.short : ''}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* UI HIỂN THỊ ĐỘC LẬP CHO CẢ 5M VÀ 15M */}
        {renderBaseBlock('5M BASE AGGREGATE', agg5mData, mc5mData, '#a3e635')}
        {renderBaseBlock('15M BASE AGGREGATE', agg15mData, mc15mData, '#34d399')}

        {/* ==========================================================
            KHỐI TÍCH HỢP VISUAL ANALYTICS (CHART & HEATMAP TỪ BINANCE)
           ========================================================== */}
        {chartDataArray.length > 0 && (
          <div style={{ ...baseStyles.card, padding: '20px', border: `1px solid ${colors.blue || '#3b82f6'}80`, marginTop: '20px' }}>
            <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong style={{ fontSize: '15px', color: colors.blue || '#3b82f6', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '6px' }}>
                📈 VISUAL ANALYTICS & VOLUME PROFILE
              </strong>
              <span style={{ fontSize: '12px', color: '#ffffff', fontWeight: '800' }}>Biểu diễn trực quan tập dữ liệu phân tích</span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', gap: '24px' }}>
              
              {/* ĐỒ THỊ 1: LINE CHART (GIÁ THEO THỜI GIAN) */}
              <div style={{ border: `1px solid ${colors.border}`, borderRadius: '6px', padding: '16px', backgroundColor: 'rgba(0,0,0,0.2)' }}>
                <div style={{ fontSize: '12px', fontWeight: 'bold', color: colors.green, marginBottom: '12px' }}>1. LINE CHART: CLOSE PRICE TIMELINE</div>
                <div style={{ width: '100%', height: '240px' }}>
                  <ResponsiveContainer>
                    <LineChart data={chartDataArray} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                      <CartesianGrid stroke="rgba(255,255,255,0.03)" />
                      <XAxis dataKey="timeLabel" tick={{ fill: colors.textSub, fontSize: 10 }} stroke={colors.border} />
                      <YAxis tick={{ fill: colors.textSub, fontSize: 10 }} stroke={colors.border} domain={['dataMin', 'dataMax']} />
                      <Tooltip contentStyle={{ background: colors.bg, borderColor: colors.border, fontSize: 12, color: '#fff' }} />
                      <Line type="monotone" dataKey="close" stroke={colors.green || '#089981'} strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* ĐỒ THỊ 2: VOLUME BAR CHART */}
              <div style={{ border: `1px solid ${colors.border}`, borderRadius: '6px', padding: '16px', backgroundColor: 'rgba(0,0,0,0.2)' }}>
                <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#3b82f6', marginBottom: '12px' }}>2. VOLUME BAR CHART: TRANSACTION INSTANT FLUX</div>
                <div style={{ width: '100%', height: '240px' }}>
                  <ResponsiveContainer>
                    <BarChart data={chartDataArray} margin={{ top: 5, right: 5, left: -10, bottom: 5 }}>
                      <CartesianGrid stroke="rgba(255,255,255,0.03)" />
                      <XAxis dataKey="timeLabel" tick={{ fill: colors.textSub, fontSize: 10 }} stroke={colors.border} />
                      <YAxis tick={{ fill: colors.textSub, fontSize: 9 }} stroke={colors.border} tickFormatter={(v) => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : (v >= 1000 ? `${(v/1000).toFixed(1)}K` : v)} />
                      <Tooltip contentStyle={{ background: colors.bg, borderColor: colors.border, fontSize: 12, color: '#fff' }} formatter={(v) => v.toLocaleString()} />
                      <Bar dataKey="volume" fill="rgba(59, 130, 246, 0.75)" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* ĐỒ THỊ 3: VOLUME PROFILE (HORIZONTAL) */}
              <div style={{ border: `1px solid ${colors.border}`, borderRadius: '6px', padding: '16px', backgroundColor: 'rgba(0,0,0,0.2)' }}>
                <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#f472b6', marginBottom: '12px' }}>3. VOLUME PROFILE: VERTICAL PRICE ACCUMULATION</div>
                <div style={{ width: '100%', height: '260px' }}>
                  <ResponsiveContainer>
                    <BarChart data={volumeProfileData} layout="vertical" margin={{ top: 5, right: 5, left: -10, bottom: 5 }}>
                      <CartesianGrid stroke="rgba(255,255,255,0.03)" />
                      <XAxis type="number" tick={{ fill: colors.textSub, fontSize: 9 }} stroke={colors.border} tickFormatter={(v) => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : (v >= 1000 ? `${(v/1000).toFixed(1)}K` : v)} />
                      <YAxis dataKey="priceLevel" type="category" tick={{ fill: '#ffffff', fontSize: 10 }} stroke={colors.border} />
                      <Tooltip contentStyle={{ background: colors.bg, borderColor: colors.border, fontSize: 12, color: '#fff' }} formatter={(v) => v.toLocaleString()} />
                      <Bar dataKey="volume" fill="rgba(244, 114, 182, 0.7)" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* ĐỒ THỊ 4: HEATMAP MATRIX CRYPTO 24/7 */}
              {heatmapMatrix && (
                <div style={{ border: `1px solid ${colors.border}`, borderRadius: '6px', padding: '16px', backgroundColor: 'rgba(0,0,0,0.2)' }}>
                  <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#f59e0b', marginBottom: '14px' }}>4. HEATMAP MATRIX: WEEKDAY VS 24H VOLUME DENSITY</div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', overflowX: 'auto', paddingBottom: '8px' }}>
                    {/* Hàng Tiêu đề trục Giờ */}
                    <div style={{ display: 'grid', gridTemplateColumns: '50px repeat(24, 1fr)', gap: '2px', textAlign: 'center', fontSize: '9px', color: colors.textSub, fontWeight: 'bold' }}>
                      <div></div>
                      {heatmapMatrix.tradingHours.map(h => <div key={`h-th-${h}`}>{h}h</div>)}
                    </div>

                    {/* Quét nạp dữ liệu lưới ô cờ */}
                    {heatmapMatrix.daysEn.map((dayEn, dIdx) => (
                      <div key={`hm-row-${dayEn}`} style={{ display: 'grid', gridTemplateColumns: '50px repeat(24, 1fr)', gap: '2px', alignItems: 'center' }}>
                        {/* Cột hiển thị tên Tiếng Việt */}
                        <div style={{ fontSize: '10px', fontWeight: 'bold', color: '#ffffff' }}>{heatmapMatrix.daysVn[dIdx]}</div>
                        
                        {heatmapMatrix.tradingHours.map(hour => {
                          const vol = heatmapMatrix.matrix[dayEn][hour];
                          const ratio = heatmapMatrix.maxVolumeInGrid > 0 ? vol / heatmapMatrix.maxVolumeInGrid : 0;
                          
                          // Mức độ đậm nhạt dựa trên Volume
                          const isZero = vol === 0;
                          const opacityAlpha = isZero ? 0.03 : Math.max(0.15, ratio);
                          const cellColor = isZero ? 'rgba(255,255,255,0.05)' : `rgba(245, 158, 11, ${opacityAlpha})`;

                          return (
                            <div 
                              key={`cell-${dayEn}-${hour}`}
                              title={`${heatmapMatrix.daysVn[dIdx]} lúc ${hour}:00 | Khối lượng: ${vol.toLocaleString()}`}
                              style={{ 
                                height: '20px', 
                                backgroundColor: cellColor,
                                borderRadius: '2px',
                                border: (!isZero && vol === heatmapMatrix.maxVolumeInGrid) ? '1px solid #fff' : 'none',
                                cursor: isZero ? 'default' : 'pointer'
                              }}
                            />
                          );
                        })}
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: '10px', color: colors.textSub, marginTop: '8px', lineHeight: '1.4' }}>
                    * Lưới nhiệt độ hoạt động Full 24/7 theo giờ hệ thống. Ô màu Cam càng đậm chứng tỏ dòng tiền tại khung giờ đó càng bùng nổ đột biến.
                  </div>
                </div>
              )}

            </div>
          </div>
        )}
        
      </div>

      <style dangerouslySetInnerHTML={{__html: `
        .matrix-scroll-container::-webkit-scrollbar { height: 8px; }
        .matrix-scroll-container::-webkit-scrollbar-track { background: ${colors.bg || '#0b0e14'}; border-radius: 4px; }
        .matrix-scroll-container::-webkit-scrollbar-thumb { background: ${colors.border || '#2b313f'}; border-radius: 4px; }
        .matrix-scroll-container::-webkit-scrollbar-thumb:hover { background: ${colors.textSub || '#94a3b8'}; }
      `}} />
    </div>
  );
}
